import React, { useEffect, useState } from 'react';
import { loadAccount, rememberCustomer, useAccount } from '../../services/customers';
import { conversationKey, getThread } from '../../services/communications';
import { APP_CALLS_LIVE, callCustomer } from '../../services/appCall';
import { avatarColours, initials } from '../Calls/avatar';
import PublicReport from '../Routes/PublicReport';
import AgentPicker from '../../components/AgentPicker';
import { CURRENT_USER } from '../../services/currentUser';
import './CustomerAccount.css';

/**
 * Customer account — the 360° view of one customer, live from Zoho (services/customers.js).
 * Opened from the search bar, the customer's name in Communications, and 360° View on the Route.
 *
 *   header         name, status, contact details + Call · Text · Email · Map
 *   status         where they are now (booked / report pending / follow up / won / lost)
 *   booking        appointment, agent, booking notes, the customer's answers
 *   report/quotes  their report (opens right here) and every quote
 *   communication  the latest texts, calls and emails → the full thread
 *   notes & tasks  Zoho notes (Inspection + Deal), follow-up calls and office tasks
 *   history        booked → inspected → quoted → follow-ups → won / lost
 * Customer progress after the sale (deposit, install…) gets added here as those parts are built.
 */

const money = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const prettyPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p || '';
};
const when = (iso) =>
  iso ? new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
const date = (iso) =>
  iso ? new Date(String(iso).length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
const timeOnly = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '');

const TONE = {
  'Inspection Booked': 'blue',
  Rebook: 'amber',
  Cancelled: 'red',
  'Report Pending': 'amber',
  'Report Sent': 'blue',
  'Follow Up': 'blue',
  'Closed Won': 'green',
  'Closed Lost': 'red',
};

const messageLine = (m) => {
  if (m.channel === 'call') return `📞 ${m.direction === 'in' ? 'Incoming' : 'Outgoing'} call${m.durationSec ? ` · ${Math.round(m.durationSec / 60) || '<1'} min` : ' · missed'}`;
  if (m.channel === 'email') return `✉️ ${m.direction === 'out' ? 'You: ' : ''}${m.subject || m.body}`;
  return `💬 ${m.direction === 'out' ? 'You: ' : ''}${m.body}`;
};

const Section = ({ title, children, right }) => (
  <section className="ca-section">
    <div className="ca-section-head">
      <h3>{title}</h3>
      {right}
    </div>
    {children}
  </section>
);

const CustomerAccount = ({ id, onBack, onOpenCommunication }) => {
  const { data, loading, error } = useAccount(id);
  const [messages, setMessages] = useState(null); // null = loading, [] = none
  const [viewingReport, setViewingReport] = useState(false);
  const [callMsg, setCallMsg] = useState('');
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    loadAccount(id);
  }, [id]);

  const c = data && data.customer;
  const key = c ? conversationKey({ servicePhone: c.phone, email: c.email }) : null;

  // the search bar lists the last few customers opened, before anything is typed
  useEffect(() => {
    if (c) rememberCustomer(c);
  }, [c]);

  // the latest of their texts, calls and emails (the same thread as Communications)
  useEffect(() => {
    if (!key) return undefined;
    let live = true;
    setMessages(null);
    getThread(key)
      .then((list) => live && setMessages(list.slice(-5).reverse()))
      .catch(() => live && setMessages([]));
    return () => {
      live = false;
    };
  }, [key]);

  // their report, full screen like the in-app preview; its ← goes back to the account
  if (viewingReport && c && c.reportToken) {
    return <PublicReport token={c.reportToken} variant="agent" onBack={() => setViewingReport(false)} />;
  }

  const [bg, fg] = avatarColours(c ? c.name : '');
  const openThread = (channel) => onOpenCommunication({ key, name: c.name, phone: c.phone, email: c.email, id: c.id, channel });
  const openTasks = data ? data.tasks.filter((t) => t.status !== 'Completed') : [];
  const doneTasks = data ? data.tasks.filter((t) => t.status === 'Completed') : [];

  return (
    <div className="ca">
      <div className="ca-bar">
        <button type="button" className="ca-back" onClick={onBack} aria-label="Back">‹</button>
        <div className="ca-bar-title">Customer account</div>
        <button type="button" className="ca-refresh" onClick={() => loadAccount(id)} disabled={loading} aria-label="Reload from Zoho">
          {loading ? '…' : '↻'}
        </button>
      </div>

      <div className="ca-scroll">
        {!c && loading && <p className="ca-empty">Loading from Zoho…</p>}
        {!c && error && (
          <p className="ca-empty error">
            {error} <button type="button" onClick={() => loadAccount(id)}>Try again</button>
          </p>
        )}

        {c && (
          <>
            {/* HEADER */}
            <div className="ca-profile">
              <div className="ca-avatar" style={{ background: bg, color: fg }}>{initials(c.name)}</div>
              <div className="ca-who">
                <div className="ca-name">
                  {c.name || 'No name'}
                  {c.priority && <span className="ca-star" title="Priority">⭐</span>}
                </div>
                {c.status && <span className={`ca-status ${TONE[c.status] || 'blue'}`}>{c.status}{c.lostReason && c.status !== 'Closed Won' && /Lost|Cancelled/.test(c.status) ? ` · ${c.lostReason}` : ''}</span>}
              </div>
            </div>
            <div className="ca-contact">
              {c.phone && <div>📱 {prettyPhone(c.phone)}</div>}
              {c.email && <div>✉️ {c.email}</div>}
              {c.address && <div>📍 {c.address}{c.postalCode ? ` · ${c.postalCode}` : ''}</div>}
              {!c.address && c.postalCode && <div>📍 {c.postalCode}</div>}
            </div>
            <div className="ca-actions">
              {c.phone &&
                (APP_CALLS_LIVE ? (
                  <button type="button" onClick={() => callCustomer(c.phone, c.name).catch((err) => setCallMsg(err.message))}>📞<span>Call</span></button>
                ) : (
                  <a href={`tel:${c.phone}`}>📞<span>Call</span></a>
                ))}
              {c.phone && <button type="button" onClick={() => openThread('sms')}>💬<span>Text</span></button>}
              {c.email && <button type="button" onClick={() => openThread('email')}>✉️<span>Email</span></button>}
              {c.address && (
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.address)}`} target="_blank" rel="noopener noreferrer">🗺<span>Map</span></a>
              )}
            </div>
            {callMsg && <div className="ca-toast" onClick={() => setCallMsg('')}>{callMsg}</div>}
            {assigning && (
              <AgentPicker
                lead={{ id: c.id, contactName: c.name, agentName: c.agent }}
                onClose={() => setAssigning(false)}
                onSaved={() => loadAccount(id)}
              />
            )}

            {/* BOOKING */}
            <Section title="Inspection">
              <div className="ca-rows">
                {c.appointmentStart && <div><span>When</span><b>{when(c.appointmentStart)}{c.appointmentEnd ? ` – ${timeOnly(c.appointmentEnd)}` : ''}</b></div>}
                <div>
                  <span>Agent</span>
                  {CURRENT_USER.admin ? (
                    <button type="button" className={`ca-agent-pick${c.agent ? '' : ' unassigned'}`} onClick={() => setAssigning(true)}>
                      {c.agent || 'Not assigned'} ✎
                    </button>
                  ) : (
                    <b>{c.agent || 'Not assigned'}</b>
                  )}
                </div>
                {c.source && <div><span>Booked by</span><b>{c.source}</b></div>}
                {c.amount != null && c.status === 'Closed Won' && <div><span>Sold</span><b>{money(c.amount)}</b></div>}
              </div>
              {c.bookingNotes && (
                <div className="ca-booking-notes">
                  <div className="ca-label">Booking notes</div>
                  <p>{c.bookingNotes}</p>
                </div>
              )}
              {(c.answers || []).length > 0 && (
                <div className="ca-answers">
                  {c.answers.map((a) => (
                    <div key={a.label}><span>{a.label}</span><b>{a.value}</b></div>
                  ))}
                </div>
              )}
              {c.findings && (c.findings.depth || c.findings.insulation || c.findings.atticSize) && (
                <div className="ca-answers">
                  {c.findings.depth && <div><span>Insulation depth</span><b>{c.findings.depth}"</b></div>}
                  {c.findings.insulation && <div><span>Insulation</span><b>{c.findings.insulation}</b></div>}
                  {c.findings.atticSize && <div><span>Attic size</span><b>{Number(c.findings.atticSize).toLocaleString('en-CA')} sq ft</b></div>}
                </div>
              )}
            </Section>

            {/* REPORT & QUOTES */}
            <Section title="Report & quotes">
              {c.reportToken ? (
                <button type="button" className="ca-wide-btn" onClick={() => setViewingReport(true)}>📄 View their report</button>
              ) : (
                <p className="ca-muted">No report sent yet.</p>
              )}
              {data.quotes.map((q) => (
                <div className="ca-quote" key={q.id}>
                  <div>
                    <b>{q.name}</b>
                    <span>{[q.number, q.stage, q.validTill && `valid till ${date(q.validTill)}`].filter(Boolean).join(' · ')}</span>
                  </div>
                  <strong>{money(q.total)}</strong>
                </div>
              ))}
            </Section>

            {/* COMMUNICATION */}
            <Section
              title="Communication"
              right={key ? <button type="button" className="ca-link" onClick={() => openThread()}>Open thread ›</button> : null}
            >
              {messages === null && <p className="ca-muted">Loading…</p>}
              {messages && !messages.length && <p className="ca-muted">No texts, calls or emails yet.</p>}
              {messages &&
                messages.map((m) => (
                  <button type="button" className="ca-msg" key={m.id} onClick={() => openThread()}>
                    <span className="ca-msg-text">{messageLine(m)}</span>
                    <span className="ca-msg-at">{date(m.at)}</span>
                  </button>
                ))}
            </Section>

            {/* NOTES & TASKS */}
            <Section title="Notes & tasks">
              {openTasks.map((t) => (
                <div className="ca-task" key={t.id}>
                  <b>☐ {t.subject}</b>
                  <span>{t.due ? `Due ${date(t.due)}` : ''}{t.description ? ` · ${t.description.split('\n')[0]}` : ''}</span>
                </div>
              ))}
              {!data.notes.length && !openTasks.length && <p className="ca-muted">No notes yet.</p>}
              {data.notes.map((n) => (
                <div className="ca-note" key={n.id}>
                  <div className="ca-note-head">
                    <b>{n.author}</b>
                    <span>{date(n.at)}{n.title ? ` · ${n.title}` : ''}</span>
                  </div>
                  <p>{n.text}</p>
                </div>
              ))}
              {doneTasks.length > 0 && <p className="ca-muted">{doneTasks.length} completed task{doneTasks.length === 1 ? '' : 's'}</p>}
            </Section>

            {/* HISTORY */}
            <Section title="History">
              <ol className="ca-timeline">
                {data.timeline.map((e, i) => (
                  <li key={`${e.at}-${i}`} className={e.planned ? 'planned' : ''}>
                    <span className="ca-dot" />
                    <div>
                      <b>{e.what}</b>
                      <span>{date(e.at)}{e.planned ? ' · upcoming' : ''}</span>
                    </div>
                  </li>
                ))}
              </ol>
            </Section>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerAccount;
